import classNames from 'classnames';
import * as _ from 'lodash-es';
import IndentClass from 'quill/formats/indent';
import QuillListItem from 'quill/formats/list';
import React from 'react';
import { createPortal } from 'react-dom';
import * as styles from './CollapsibleListItem.module.scss';
import ListCollapseFormat from './ListCollapse';
import Scroll from './Scroll';
import SoftBreak from './SoftBreak';
import SVGIcon from '../../components/SVGIcon';
import caretBold from '../../icons/non-prefilled/16x16/caretBold.svg';
import { useGutterConfig } from '../GutterConfigProvider';

const CollapsedIcon = () => {
  const config = useGutterConfig();

  return (
    <div
      className={classNames(styles.collapsedIconContainer, {
        [styles.isReorderingEnabled]: config.reorderingEnabled,
      })}
    >
      <span className={styles.collapsedIcon}>
        <SVGIcon size={8}>{caretBold}</SVGIcon>
      </span>
    </div>
  );
};

class CollapsibleListItem extends QuillListItem {
  static readonly INDENT_MAP_ATTR_NAME = 'data-indent-map';
  static readonly ID_ATTR_NAME = 'data-collapsible-list-item-id';

  readonly id: string;

  constructor(scroll: Scroll, domNode: HTMLElement) {
    super(scroll, domNode);
    this.id = _.uniqueId('collapsible-list-item-');
    this.updateIndentMap();
    this.domNode.classList.add(styles.container);
    this.domNode.setAttribute(CollapsibleListItem.ID_ATTR_NAME, this.id);
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  format(name: string, value: string) {
    super.format(name, value);
    this.updateIndentMap();
  }

  setIsGloballyCollapsed(value: boolean) {
    this.format(ListCollapseFormat.attrName, value ? 'true' : '');
  }

  isGloballyCollapsed() {
    return ListCollapseFormat.value(this.domNode);
  }

  isSoftLine() {
    return SoftBreak.value(this.domNode);
  }

  getIndent(): number {
    return IndentClass.value(this.domNode) ?? 0;
  }

  private updateIndentMap() {
    const currentIndent = this.getIndent();

    // This string contains information about the element indent level
    // formatted in a way that's easy to query with CSS. Examples:
    // - indent 1: "0 1 -2 -3 -4 -5 -6 -7 -8"
    // - indent 3: "0 1 2 3 -4 -5 -6 -7 -8"
    // - indent 6: "0 1 2 3 4 5 6 -7 -8"
    const indentMap = new Array(9)
      .fill(0)
      .map((_item, i) => `${currentIndent < i ? '-' : ''}${i}`)
      .join(' ');

    this.domNode.setAttribute(
      CollapsibleListItem.INDENT_MAP_ATTR_NAME,
      `${indentMap}`,
    );
  }

  renderPortal() {
    return this.uiNode && createPortal(<CollapsedIcon />, this.uiNode);
  }
}

export default CollapsibleListItem;
